import { FC, memo } from "react";
import { observer } from "mobx-react";
import { ButtonGroup, IconButton, Typography } from "@mui/material";

import Row from "../../../ui/core/Row";
import { AttachedFile } from "../../../../types/chat";
import { AttachmentIcon, SendIcon } from "../../../icons";
import { useChatStore } from "../../../../hooks/useChatStore";
import StopIcon from "../../../icons/StopIcon";
import { useCapabilities } from "../../../../contexts/permissions/useCapabilities";
import MockLlmToggle from "./MockLlmToggle";
import { useClaimsStore } from "../../../../hooks/useClaimsStore";

interface ChatInputActionsProps {
  uploadedFiles: AttachedFile[];
  isSubmitDisabled: boolean;
  predefaultSubmitState: boolean;
  onAddAttachment: () => void;
}

const ChatInputActions: FC<ChatInputActionsProps> = observer(
  ({
    uploadedFiles,
    onAddAttachment,
    isSubmitDisabled: externalSubmitState,
    predefaultSubmitState,
  }) => {
    const chatStore = useChatStore();
    const { claimActivePredefaultQuestion } = useClaimsStore();
    const { canUploadAttachments } = useCapabilities();

    const isSubmitDisabled = claimActivePredefaultQuestion
      ? predefaultSubmitState
      : externalSubmitState;

    return (
      <Row sx={{ justifyContent: "space-between", width: "100%" }}>
        {/* Upload files */}
        {canUploadAttachments ? (
          <ButtonGroup
            onClick={onAddAttachment}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              color: "text.secondary",
              "&:hover": { color: "primary.main" },
            }}
          >
            <IconButton
              aria-label="Add attachment"
              size="small"
              sx={{
                borderRadius: "4px",
                border: "1px solid",
                borderColor: "divider",
                width: "32px",
                height: "32px",
              }}
            >
              <AttachmentIcon />
            </IconButton>
            <Typography
              variant="h5"
              color="inherit"
              fontWeight={500}
              component="span"
              sx={{ whiteSpace: "nowrap" }}
            >
              {uploadedFiles.length > 0
                ? `${uploadedFiles.length} file(s) attached`
                : "Add attachment"}
            </Typography>
          </ButtonGroup>
        ) : null}

        <Row
          sx={{ gridGap: 6, justifyContent: "flex-end", alignItems: "center" }}
        >
          <MockLlmToggle />
          {chatStore.isQuestioning && !claimActivePredefaultQuestion ? (
            <IconButton
              aria-label="Abort"
              disabled={chatStore.isAborting}
              onClick={() => chatStore.abortQuestion()}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
                "&:disabled": {
                  opacity: 0.5,
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                },
              }}
            >
              <StopIcon />
            </IconButton>
          ) : (
            <IconButton
              aria-label="Send message"
              type={isSubmitDisabled ? "button" : "submit"}
              disabled={isSubmitDisabled}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background:
                  "linear-gradient(87.38deg, #272366 0.42%, #9158CE 100%)",
                color: "primary.contrastText",
                "&:hover": {
                  background:
                    "linear-gradient(87.38deg, #272366 0.42%, #9158CE 100%)",
                  filter: "brightness(0.9)",
                },
                "&:disabled": {
                  opacity: 0.5,
                  background:
                    "linear-gradient(87.38deg, #272366 0.42%, #9158CE 100%)",
                  color: "primary.contrastText",
                },
              }}
            >
              <SendIcon />
            </IconButton>
          )}
        </Row>
      </Row>
    );
  },
);

export default memo(ChatInputActions);
